import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  Chat,
  ChatAccessLevel,
  ChatContextUsage,
  ChatUsage,
  Message,
  ModelId,
} from 'chat-shared'
import { api } from '../api/client.js'
import { getChatSocket } from '../api/socket.js'

export type StreamingToolCall = {
  toolCallId: string
  toolName: string
  args: unknown
  status: 'running' | 'done' | 'error'
  resultPreview?: string
}

export type ChatSessionError = {
  message: string
  /** Technische Details (z. B. Original-Fehler des LLM-Providers), aufklappbar. */
  details?: string
}

export type StreamingTurn = {
  id: string
  content: string
  reasoning: string
  toolCalls: Array<StreamingToolCall>
}

export type ChatSessionState = {
  chat?: Chat & { access: ChatAccessLevel }
  messages: Array<Message>
  streamingTurns: Array<StreamingTurn>
  isStreaming: boolean
  error?: ChatSessionError
  /** Kontext-Füllstand aus GET /api/chats/:id — aktualisiert nach jeder Antwort. */
  contextUsage?: ChatContextUsage
  /** Token-Verbrauch der letzten Antwort (aus chat:done), bis zum Reload der Seite. */
  lastUsage?: ChatUsage
  isCompacting: boolean
  send: (content: string, model: ModelId) => void
  abort: () => void
  rename: (title: string) => Promise<void>
  /** Komprimiert den Kontext serverseitig (LLM-Zusammenfassung). */
  compact: (model: ModelId) => Promise<void>
}

const upsertTurn = (
  turns: Array<StreamingTurn>,
  id: string,
  update: (turn: StreamingTurn) => StreamingTurn,
): Array<StreamingTurn> => {
  const existing = turns.find((turn) => turn.id === id)
  const base: StreamingTurn = existing ?? { id, content: '', reasoning: '', toolCalls: [] }
  const updated = update(base)
  return existing
    ? turns.map((turn) => (turn.id === id ? updated : turn))
    : [...turns, updated]
}

export const useChatSession = (chatId: string | undefined): ChatSessionState => {
  const [chat, setChat] = useState<(Chat & { access: ChatAccessLevel }) | undefined>(undefined)
  const [messages, setMessages] = useState<Array<Message>>([])
  const [streamingTurns, setStreamingTurns] = useState<Array<StreamingTurn>>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<ChatSessionError | undefined>(undefined)
  const [contextUsage, setContextUsage] = useState<ChatContextUsage | undefined>(undefined)
  const [lastUsage, setLastUsage] = useState<ChatUsage | undefined>(undefined)
  const [isCompacting, setIsCompacting] = useState(false)
  const chatIdRef = useRef(chatId)
  chatIdRef.current = chatId

  const reload = useCallback(async (id: string) => {
    const detail = await api.getChat(id)
    if (chatIdRef.current !== id) return
    setChat(detail)
    setMessages(detail.messages)
    setContextUsage(detail.contextUsage)
  }, [])

  useEffect(() => {
    if (!chatId) return
    setChat(undefined)
    setMessages([])
    setStreamingTurns([])
    // isStreaming gehört zum vorherigen Chat — ohne Reset bliebe die Eingabe
    // gesperrt, weil dessen `chat:done` hier per chatId-Filter ignoriert wird.
    setIsStreaming(false)
    setError(undefined)
    setContextUsage(undefined)
    setLastUsage(undefined)
    setIsCompacting(false)

    void reload(chatId)

    const socket = getChatSocket()
    socket.emit('chat:join', { chatId })

    const onReasoningDelta: Parameters<typeof socket.on<'chat:reasoning-delta'>>[1] = (
      payload,
    ) => {
      if (payload.chatId !== chatId) return
      setIsStreaming(true)
      setStreamingTurns((turns) =>
        upsertTurn(turns, payload.messageId, (turn) => ({
          ...turn,
          reasoning: turn.reasoning + payload.delta,
        })),
      )
    }
    const onTextDelta: Parameters<typeof socket.on<'chat:text-delta'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setIsStreaming(true)
      setStreamingTurns((turns) =>
        upsertTurn(turns, payload.messageId, (turn) => ({
          ...turn,
          content: turn.content + payload.delta,
        })),
      )
    }
    const onToolCall: Parameters<typeof socket.on<'chat:tool-call'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setStreamingTurns((turns) =>
        upsertTurn(turns, payload.messageId, (turn) => ({
          ...turn,
          toolCalls: [
            ...turn.toolCalls,
            {
              toolCallId: payload.toolCallId,
              toolName: payload.toolName,
              args: payload.args,
              status: 'running',
            },
          ],
        })),
      )
    }
    const onToolResult: Parameters<typeof socket.on<'chat:tool-result'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setStreamingTurns((turns) =>
        upsertTurn(turns, payload.messageId, (turn) => ({
          ...turn,
          toolCalls: turn.toolCalls.map((call) =>
            call.toolCallId === payload.toolCallId
              ? {
                  ...call,
                  status: payload.isError ? 'error' : 'done',
                  resultPreview: payload.resultPreview,
                }
              : call,
          ),
        })),
      )
    }
    const onDone: Parameters<typeof socket.on<'chat:done'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setIsStreaming(false)
      setStreamingTurns([])
      setLastUsage(payload.usage)
      void reload(chatId)
    }
    const onTitle: Parameters<typeof socket.on<'chat:title'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setChat((prev) => (prev ? { ...prev, title: payload.title } : prev))
    }
    const onError: Parameters<typeof socket.on<'chat:error'>>[1] = (payload) => {
      if (payload.chatId !== chatId) return
      setIsStreaming(false)
      setError({ message: payload.message, details: payload.details })
    }

    socket.on('chat:reasoning-delta', onReasoningDelta)
    socket.on('chat:text-delta', onTextDelta)
    socket.on('chat:tool-call', onToolCall)
    socket.on('chat:tool-result', onToolResult)
    socket.on('chat:done', onDone)
    socket.on('chat:title', onTitle)
    socket.on('chat:error', onError)

    return () => {
      socket.off('chat:reasoning-delta', onReasoningDelta)
      socket.off('chat:text-delta', onTextDelta)
      socket.off('chat:tool-call', onToolCall)
      socket.off('chat:tool-result', onToolResult)
      socket.off('chat:done', onDone)
      socket.off('chat:title', onTitle)
      socket.off('chat:error', onError)
    }
  }, [chatId, reload])

  const send = useCallback(
    (content: string, model: ModelId) => {
      if (!chatId) return
      setError(undefined)
      setIsStreaming(true)
      // Optimistisch anzeigen, bevor die Server-Antwort zurückkommt; nach
      // `chat:done` ersetzt der REST-Reload die Liste durch den echten Stand.
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          chatId,
          role: 'user',
          content,
          createdAt: new Date().toISOString(),
        },
      ])
      getChatSocket().emit('chat:send', { chatId, content, model })
    },
    [chatId],
  )

  const abort = useCallback(() => {
    if (!chatId) return
    getChatSocket().emit('chat:abort', { chatId })
  }, [chatId])

  const compact = useCallback(
    async (model: ModelId): Promise<void> => {
      if (!chatId) return
      setError(undefined)
      setIsCompacting(true)
      try {
        const result = await api.compactChat(chatId, model)
        if (chatIdRef.current !== chatId) return
        setContextUsage(result.contextUsage)
        await reload(chatId)
      } catch (err) {
        if (chatIdRef.current === chatId) {
          setError({
            message: 'Komprimieren fehlgeschlagen',
            details: err instanceof Error ? err.message : undefined,
          })
        }
      } finally {
        if (chatIdRef.current === chatId) setIsCompacting(false)
      }
    },
    [chatId, reload],
  )

  // Optimistisches Update; das `chat:title`-Event vom Server bestätigt den
  // Stand danach idempotent (und erreicht auch andere offene Tabs/Clients).
  const rename = useCallback(
    async (title: string): Promise<void> => {
      if (!chatId) return
      await api.renameChat(chatId, title)
      setChat((prev) => (prev ? { ...prev, title } : prev))
    },
    [chatId],
  )

  return {
    chat,
    messages,
    streamingTurns,
    isStreaming,
    error,
    contextUsage,
    lastUsage,
    isCompacting,
    send,
    abort,
    rename,
    compact,
  }
}
